// Hook for fetching boutiques/businesses from Firestore
import { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  query, 
  where, 
  limit,
  onSnapshot,
  doc,
  GeoPoint
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { coordsFromAddress, coordsFromFirestoreLocation } from '../lib/geocode';
import { sortFeaturedBoutiques } from '../lib/featuredHero';
import { DEFAULT_DELIVERY_RADIUS_MILES, distanceMiles } from '../lib/geo';

export interface Boutique {
  id: string;
  name: string;
  description: string;
  logo: string;
  coverImage: string;
  /** Dedicated homepage hero image when the store is featured. Falls back to coverImage. */
  featuredImage?: string;
  /** Lower numbers appear first in the homepage featured carousel. */
  featuredOrder?: number;
  address: string | { street: string; city: string; state: string; zipCode: string; unit?: string; lat?: number; lng?: number };
  formattedAddress?: string;
  location: GeoPoint;
  rating: number;
  reviewCount: number;
  categories: string[];
  hours: string | {
    [key: string]: { open: string; close: string } | null;
  };
  closedNow?: boolean;
  deliveryRadius: number;
  /** Minutes the boutique needs to pack an order, set during onboarding. */
  prepTimeEstimate?: number;
  active: boolean;
  featured: boolean;
}

export interface BoutiqueWithDistance extends Boutique {
  /** Miles from the shopper. Null when either side has no coordinates. */
  distanceMiles: number | null;
  /** False only when we know the shopper sits outside the delivery radius. */
  deliversToUser: boolean;
}

/** Upper bound when we need the full set locally to rank boutiques by distance. */
const GEO_FETCH_CAP = 100;

export function boutiqueCoords(boutique: Boutique) {
  return (
    coordsFromFirestoreLocation(boutique.location as any) ||
    (typeof boutique.address === 'object' ? coordsFromAddress(boutique.address) : null)
  );
}

export function withDistance(
  boutiques: Boutique[],
  origin: { lat: number; lng: number } | null
): BoutiqueWithDistance[] {
  return boutiques.map((boutique) => {
    const coords = origin ? boutiqueCoords(boutique) : null;
    if (!origin || !coords) {
      return { ...boutique, distanceMiles: null, deliversToUser: true };
    }

    const miles = distanceMiles(origin.lat, origin.lng, coords.lat, coords.lng);
    const radius = boutique.deliveryRadius || DEFAULT_DELIVERY_RADIUS_MILES;
    return { ...boutique, distanceMiles: miles, deliversToUser: miles <= radius };
  });
}

// Fetch boutiques with optional filters
export function useBoutiques(options?: {
  featured?: boolean;
  limitCount?: number;
  /** Shopper coordinates used to rank and filter by delivery distance. */
  near?: { lat: number; lng: number } | null;
  /** Hide boutiques that cannot deliver to `near`. Defaults to true. */
  filterByDelivery?: boolean;
}) {
  const [boutiques, setBoutiques] = useState<Boutique[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const featured = options?.featured;
  const limitCount = options?.limitCount;
  const nearLat = options?.near?.lat ?? null;
  const nearLng = options?.near?.lng ?? null;
  const filterByDelivery = options?.filterByDelivery ?? true;

  const hasOrigin = nearLat !== null && nearLng !== null;

  useEffect(() => {
    let q = query(
      collection(db, 'businesses'),
      where('active', '==', true)
    );

    if (featured) {
      q = query(q, where('featured', '==', true));
    }

    // With an origin we sort and filter locally, so a Firestore-side limit
    // would cut off boutiques that are actually the closest ones.
    const fetchLimit = hasOrigin ? GEO_FETCH_CAP : limitCount;
    if (fetchLimit) {
      q = query(q, limit(fetchLimit));
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const boutiquesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Boutique[];
        setBoutiques(boutiquesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching boutiques:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [featured, limitCount, hasOrigin]);

  const ranked = useMemo(() => {
    const origin = hasOrigin ? { lat: nearLat!, lng: nearLng! } : null;
    let result = withDistance(boutiques, origin);

    if (origin) {
      if (filterByDelivery) {
        result = result.filter((b) => b.deliversToUser);
      }
      result = result.sort((a, b) => {
        if (a.distanceMiles === null) return 1;
        if (b.distanceMiles === null) return -1;
        return a.distanceMiles - b.distanceMiles;
      });
    }

    return limitCount ? result.slice(0, limitCount) : result;
  }, [boutiques, hasOrigin, nearLat, nearLng, filterByDelivery, limitCount]);

  return { boutiques: ranked, loading, error };
}

// Featured boutiques for the homepage hero carousel, in admin-defined order
export function useFeaturedBoutiques() {
  const [boutiques, setBoutiques] = useState<Boutique[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'businesses'),
      where('active', '==', true),
      where('featured', '==', true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Boutique[];
        setBoutiques(sortFeaturedBoutiques(data));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching featured boutiques:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { boutiques, loading, error };
}

// Fetch single boutique by ID
export function useBoutique(boutiqueId: string | null) {
  const [boutique, setBoutique] = useState<Boutique | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!boutiqueId) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'businesses', boutiqueId),
      (doc) => {
        if (doc.exists()) {
          setBoutique({ id: doc.id, ...doc.data() } as Boutique);
        } else {
          setBoutique(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching boutique:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [boutiqueId]);

  return { boutique, loading, error };
}
